import {createTheme, ThemeOptions} from '@mui/material'

import {componentsOverrides} from './componentsOverrides'
import {
  darkModeShadows,
  hpDarkPalette,
  hpLightPalette,
  lightModeShadows,
  typographyTokens,
  shapeTokens
} from './designTokens'
import {
  fundamentalCssVarPalette,
  fundamentalDarkPalette,
  fundamentalLightBlueContrastPalette
} from './fundamentalAlignedTokens'

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    formLabel: true
    captionBold: true
  }
}

declare module '@mui/material/styles' {
  interface TypeText {
    primarySoft: string
    secondarySoft: string
  }
  interface TypeTextOptions {
    primarySoft?: string
    secondarySoft?: string
  }
}

// Light theme
const hpLightThemeOptions: ThemeOptions = {
  palette: hpLightPalette,
  shadows: lightModeShadows,
  shape: shapeTokens,
  typography: typographyTokens,
  components: componentsOverrides
}

export const hpTheme = createTheme(hpLightThemeOptions)

// Dark theme
const darkThemeOptions: ThemeOptions = {
  palette: hpDarkPalette,
  shadows: darkModeShadows,
  shape: shapeTokens,
  typography: typographyTokens,
  components: componentsOverrides
}
export const hpDarkTheme = createTheme(darkThemeOptions)

// Fundamental-aligned light theme.
// Palette is hardcoded to ThemeLightBlueGrey token values so MUI's built-in
// components and `alpha()`/`lighten()`/`darken()` utilities resolve correctly.
const hpFundamentalLightThemeOptions: ThemeOptions = {
  palette: fundamentalCssVarPalette,
  shadows: lightModeShadows,
  shape: shapeTokens,
  typography: typographyTokens,
  components: componentsOverrides
}
export const hpFundamentalLightTheme = createTheme(hpFundamentalLightThemeOptions)

// Fundamental-aligned dark theme.
// Palette is hardcoded to ThemeDarkBlue token values, mirroring the light theme.
const hpFundamentalDarkThemeOptions: ThemeOptions = {
  palette: fundamentalDarkPalette,
  shadows: darkModeShadows,
  shape: shapeTokens,
  typography: typographyTokens,
  components: componentsOverrides
}
export const hpFundamentalDarkTheme = createTheme(hpFundamentalDarkThemeOptions)

// Fundamental-aligned "contrast" theme (ThemeLightBlueContrast).
// Apply around regions rendered on the blue primary surface so token-based
// styles resolve to on-blue colours. Dropdown surfaces (menu/select/autocomplete
// popovers) are kept as light cards so their options stay readable off the blue.
const lightMenuSurface = {
  backgroundColor: hpFundamentalLightTheme.palette.background.paper,
  color: hpFundamentalLightTheme.palette.text.primary
}
const hpFundamentalContrastThemeOptions: ThemeOptions = {
  palette: fundamentalLightBlueContrastPalette,
  shadows: lightModeShadows,
  shape: shapeTokens,
  typography: typographyTokens,
  components: {
    ...componentsOverrides,
    MuiPopover: {
      ...componentsOverrides.MuiPopover,
      styleOverrides: {
        ...componentsOverrides.MuiPopover?.styleOverrides,
        paper: lightMenuSurface
      }
    },
    MuiAutocomplete: {
      ...componentsOverrides.MuiAutocomplete,
      styleOverrides: {
        ...componentsOverrides.MuiAutocomplete?.styleOverrides,
        paper: lightMenuSurface
      }
    },
    MuiMenuItem: {
      ...componentsOverrides.MuiMenuItem,
      styleOverrides: {
        ...componentsOverrides.MuiMenuItem?.styleOverrides,
        root: {
          color: hpFundamentalLightTheme.palette.text.primary,
          '&:hover': {
            backgroundColor: hpFundamentalLightTheme.palette.action.hover
          },
          '&.Mui-selected': {
            backgroundColor: hpFundamentalLightTheme.palette.action.selected,
            '&:hover': {
              backgroundColor: hpFundamentalLightTheme.palette.action.selected
            }
          }
        }
      }
    }
  }
}
export const hpFundamentalContrastTheme = createTheme(hpFundamentalContrastThemeOptions)
